import { useEffect, useMemo, useRef, useState } from 'react'
import { FileAudio, Loader2, Mic, Square } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { friendlyMessage } from '@/api/http'
import { getTranscribeStatus, transcribeAudio, type TranscribeStatusResponse } from '@/api/modelSettingsApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { isAudioLongEnough, mergeTranscribedText, type VoiceInsertMode } from './voiceInputUtils'

const VOICE_INPUT_ENABLED_KEY = 'nf.voiceInput.enabled'

interface VoiceInputButtonProps {
  value: string
  onChange: (value: string) => void
  language?: string
  disabled?: boolean
  className?: string
}

function readVoicePreference(): boolean {
  if (typeof window === 'undefined') return true
  return window.localStorage.getItem(VOICE_INPUT_ENABLED_KEY) !== 'false'
}

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

async function readAudioDurationMs(file: File): Promise<number | null> {
  if (typeof document === 'undefined' || typeof URL === 'undefined') return null
  const url = URL.createObjectURL(file)
  try {
    const audio = document.createElement('audio')
    audio.preload = 'metadata'
    return await new Promise((resolve) => {
      audio.onloadedmetadata = () => {
        resolve(Number.isFinite(audio.duration) ? audio.duration * 1000 : null)
      }
      audio.onerror = () => resolve(null)
      audio.src = url
    })
  } finally {
    URL.revokeObjectURL(url)
  }
}

export function VoiceInputButton({ value, onChange, language, disabled = false, className }: VoiceInputButtonProps) {
  const { t } = useTranslation()
  const [status, setStatus] = useState<TranscribeStatusResponse | null>(null)
  const [statusLoading, setStatusLoading] = useState(false)
  const [preferenceEnabled, setPreferenceEnabled] = useState(readVoicePreference)
  const [insertMode, setInsertMode] = useState<VoiceInsertMode>('append')
  const [isRecording, setIsRecording] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [elapsedMs, setElapsedMs] = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const startedAtRef = useRef<number>(0)
  const timerRef = useRef<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    let cancelled = false
    setStatusLoading(true)
    getTranscribeStatus()
      .then((nextStatus) => {
        if (!cancelled) setStatus(nextStatus)
      })
      .catch(() => {
        if (!cancelled) setStatus({ available: false })
      })
      .finally(() => {
        if (!cancelled) setStatusLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current)
      streamRef.current?.getTracks().forEach((track) => track.stop())
    }
  }, [])

  const statusKnown = status !== null
  const available = Boolean(status?.available)
  const controlsDisabled = disabled || statusLoading || !available || !preferenceEnabled || isSubmitting
  const tooltip = useMemo(() => {
    if (statusKnown && !available) return t('voiceInput.adminTooltip')
    if (!preferenceEnabled) return t('voiceInput.preferenceOffTooltip')
    return undefined
  }, [available, preferenceEnabled, statusKnown, t])

  const setPreference = (enabled: boolean) => {
    setPreferenceEnabled(enabled)
    window.localStorage.setItem(VOICE_INPUT_ENABLED_KEY, enabled ? 'true' : 'false')
  }

  const applyTranscribedText = (text: string) => {
    const merged = mergeTranscribedText(value, text, insertMode)
    onChange(merged)
  }

  const submitAudio = async (audio: Blob) => {
    setIsSubmitting(true)
    try {
      const result = await transcribeAudio(audio, language)
      applyTranscribedText(result.text)
      toast.success(t('voiceInput.transcribed'))
    } catch (error) {
      toast.error(friendlyMessage(error, t('voiceInput.failed')))
    } finally {
      setIsSubmitting(false)
    }
  }

  const startRecording = async () => {
    if (controlsDisabled || isRecording) return
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error(t('voiceInput.unsupported'))
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      streamRef.current = stream
      chunksRef.current = []
      startedAtRef.current = Date.now()
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      }
      recorder.onstop = () => {
        const durationMs = Date.now() - startedAtRef.current
        stream.getTracks().forEach((track) => track.stop())
        streamRef.current = null
        if (!isAudioLongEnough(durationMs)) {
          toast.error(t('voiceInput.tooShort'))
          return
        }
        const blob = new File(chunksRef.current, 'voice.webm', { type: recorder.mimeType || 'audio/webm' })
        void submitAudio(blob)
      }
      mediaRecorderRef.current = recorder
      recorder.start()
      setElapsedMs(0)
      setIsRecording(true)
      timerRef.current = window.setInterval(() => setElapsedMs(Date.now() - startedAtRef.current), 250)
    } catch {
      toast.error(t('voiceInput.microphoneFailed'))
    }
  }

  const stopRecording = () => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') return
    mediaRecorderRef.current.stop()
    mediaRecorderRef.current = null
    setIsRecording(false)
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  const importAudio = async (file: File | undefined) => {
    if (!file || controlsDisabled || isRecording) return
    const durationMs = await readAudioDurationMs(file)
    if (durationMs !== null && !isAudioLongEnough(durationMs)) {
      toast.error(t('voiceInput.tooShort'))
      return
    }
    await submitAudio(file)
  }

  return (
    <TooltipProvider>
      <div className={cn('flex flex-wrap items-center gap-2', className)}>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0]
            event.target.value = ''
            void importAudio(file)
          }}
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                type="button"
                size="sm"
                variant={isRecording ? 'destructive' : 'outline'}
                disabled={controlsDisabled && !isRecording}
                onClick={isRecording ? stopRecording : startRecording}
              >
                {isRecording ? <Square className="size-4" /> : isSubmitting ? <Loader2 className="size-4 animate-spin" /> : <Mic className="size-4" />}
                {isRecording ? t('voiceInput.stop', { time: formatElapsed(elapsedMs) }) : t('voiceInput.record')}
              </Button>
            </span>
          </TooltipTrigger>
          {tooltip ? <TooltipContent>{tooltip}</TooltipContent> : null}
        </Tooltip>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={controlsDisabled || isRecording}
          onClick={() => fileInputRef.current?.click()}
        >
          <FileAudio className="size-4" />
          {t('voiceInput.importAudio')}
        </Button>
        <Select value={insertMode} onValueChange={(nextValue) => setInsertMode(nextValue as VoiceInsertMode)}>
          <SelectTrigger className="h-8 w-28 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="append">{t('voiceInput.insertModes.append')}</SelectItem>
            <SelectItem value="replace">{t('voiceInput.insertModes.replace')}</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={preferenceEnabled}
            disabled={!available}
            onChange={(event) => setPreference(event.target.checked)}
          />
          {t('voiceInput.preference')}
        </label>
        {statusKnown && !available ? <Badge variant="secondary">{t('voiceInput.disabledBadge')}</Badge> : null}
      </div>
    </TooltipProvider>
  )
}
